import { SoilAnalyteUnit, type SoilSampling } from 'api-validations/locutus'
import { isNonNil } from 'utils'

import { useLocalization } from '../localization'

/**
 * How each unit is written on screen.
 *
 * Not run through `localizeUnit`: it relabels `lbs/ac` as `kg/ha` and `in` as
 * `cm` for a metric operation, which is correct for a value Panorama computed
 * and wrong for one a lab reported — the number here is whatever the lab
 * measured, in the unit it measured it in, and converting it is not ours to do.
 */
export const SOIL_UNIT_SYMBOL: Record<SoilAnalyteUnit, string> = {
  [SoilAnalyteUnit.Ppm]: 'ppm',
  [SoilAnalyteUnit.Percent]: '%',
  [SoilAnalyteUnit.MeqPer100g]: 'meq/100g',
  [SoilAnalyteUnit.MmhosPerCm]: 'mmhos/cm',
  [SoilAnalyteUnit.LbsPerAcre]: 'lbs/ac',
  [SoilAnalyteUnit.KgPerHectare]: 'kg/ha'
}

const ANALYTE_LABEL_PATH = 'map.soilSamples.analytes'
const METHOD_LABEL_PATH = 'map.soilSamples.methods'

/**
 * Names an analyte the way a grower picks it out of a layer list.
 *
 * The extraction method is part of the name because it is part of what was
 * measured: two extractions of the same analyte produce numbers that cannot be
 * compared, and a dataset may report both.
 */
export const useSoilAnalyteLabel = () => {
  const { tByPath } = useLocalization()

  return (descriptor: SoilSampling.AnalyteDescriptor): string => {
    const analyte = tByPath(`${ANALYTE_LABEL_PATH}.${descriptor.analyte}`, {
      defaultValue: descriptor.analyte
    })
    if (descriptor.method === null) return analyte

    const method = tByPath(`${METHOD_LABEL_PATH}.${descriptor.method}`, {
      defaultValue: descriptor.method
    })
    return `${analyte} (${method})`
  }
}

/**
 * The depth a dataset was sampled at, or `null` for one that did not say.
 *
 * Both bounds appear even though they share a unit, because the interval is the
 * meaningful thing: a 0-8 reading and an 8-24 reading of the same analyte are
 * different measurements of the same field.
 */
export const useSoilDepthLabel = () => {
  const { formatMetric } = useLocalization()

  return (depth: SoilSampling.Dataset['depth']): string | null => {
    if (!isNonNil(depth)) return null
    const top = formatMetric({ value: depth.top })
    const bottom = formatMetric({ value: depth.bottom })
    return `${top}-${bottom} ${depth.unit}`
  }
}

export const useSoilDatasetTitle = () => {
  const { t } = useLocalization()
  const depthLabel = useSoilDepthLabel()

  return (dataset: Pick<SoilSampling.Dataset, 'season' | 'depth'>): string => {
    const depth = depthLabel(dataset.depth)
    return depth === null
      ? t(($) => $.map.soilSamples.datasetTitle, { season: dataset.season })
      : t(($) => $.map.soilSamples.datasetTitleWithDepth, { season: dataset.season, depth })
  }
}
